import { supabase } from '@/lib/supabase'
import type { World, WorldCreate, WorldUpdate } from '@/types'
import { scheduleInitialElections } from '@/lib/electionScheduling'

export async function fetchWorlds(): Promise<World[]> {
  const { data, error } = await supabase
    .from('worlds')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return data as World[]
}

export async function fetchWorld(id: string): Promise<World> {
  const { data, error } = await supabase.from('worlds').select('*').eq('id', id).single()

  if (error) {
    throw error
  }

  return data as World
}

export async function createWorld(world: WorldCreate): Promise<World> {
  const { data, error } = await supabase.from('worlds').insert(world).select().single()

  if (error) {
    throw error
  }

  return data as World
}

export async function updateWorld(id: string, updates: WorldUpdate): Promise<World> {
  const { data, error } = await supabase
    .from('worlds')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as World
}

export async function deleteWorld(id: string): Promise<void> {
  const { error } = await supabase.from('worlds').delete().eq('id', id)

  if (error) {
    throw error
  }
}

/**
 * Activate a world, transitioning it from 'setup' to 'active' status
 *
 * This also schedules initial elections for all nations in the world.
 *
 * @param worldId - ID of the world to activate
 * @returns The updated world
 * @throws Error if world is not in 'setup' status
 */
export async function activateWorld(worldId: string): Promise<World> {
  // Fetch current world state
  const world = await fetchWorld(worldId)

  // Validate world can be activated
  if (world.status !== 'setup') {
    throw new Error(
      `World ${worldId} cannot be activated: current status is '${world.status}', expected 'setup'`
    )
  }

  console.log(`[World Activation] Activating world ${worldId} (${world.name})`)

  // Update world status to 'active'
  const updatedWorld = await updateWorld(worldId, { status: 'active' })

  // Schedule initial elections for all nations
  try {
    const electionResult = await scheduleInitialElections(worldId)
    console.log(
      `[World Activation] Scheduled ${electionResult.electionsScheduled.length} initial elections ` +
        `for world ${worldId}`
    )
  } catch (error) {
    console.error('[World Activation] Failed to schedule initial elections:', error)
    // Don't throw - world is still activated, elections can be scheduled manually
  }

  console.log(`[World Activation] World ${worldId} activated successfully`)
  return updatedWorld
}

/**
 * Pause a world, transitioning it from 'active' to 'paused' status
 *
 * @param worldId - ID of the world to pause
 * @returns The updated world
 * @throws Error if world is not in 'active' status
 */
export async function pauseWorld(worldId: string): Promise<World> {
  const world = await fetchWorld(worldId)

  if (world.status !== 'active') {
    throw new Error(
      `World ${worldId} cannot be paused: current status is '${world.status}', expected 'active'`
    )
  }

  console.log(`[World] Pausing world ${worldId} (${world.name})`)
  return updateWorld(worldId, { status: 'paused' })
}

/**
 * Resume a paused world, transitioning it from 'paused' to 'active' status
 *
 * @param worldId - ID of the world to resume
 * @returns The updated world
 * @throws Error if world is not in 'paused' status
 */
export async function resumeWorld(worldId: string): Promise<World> {
  const world = await fetchWorld(worldId)

  if (world.status !== 'paused') {
    throw new Error(
      `World ${worldId} cannot be resumed: current status is '${world.status}', expected 'paused'`
    )
  }

  console.log(`[World] Resuming world ${worldId} (${world.name})`)
  return updateWorld(worldId, { status: 'active' })
}

/**
 * Complete a world, transitioning it to 'completed' status
 *
 * @param worldId - ID of the world to complete
 * @returns The updated world
 */
export async function completeWorld(worldId: string): Promise<World> {
  const world = await fetchWorld(worldId)

  if (world.status === 'completed') {
    throw new Error(`World ${worldId} is already completed`)
  }

  console.log(`[World] Completing world ${worldId} (${world.name})`)
  return updateWorld(worldId, { status: 'completed' })
}
